import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	setLoading,
	storeClientDetails,
	storeSearchContact
} from 'store/reducers/mainSlice';
import { FaPlus, FaSearch, FaSpinner } from 'react-icons/fa';
import Filter from 'ui-component/filter-menu/Filter';
import moment from 'moment';
import { Skeleton, Tooltip } from '@mui/material';
import toast from 'react-hot-toast';

import PermissionAllowed from 'ui-component/permissions/PermissionAllowed';
// import DeleteConfirmation from 'ui-component/confirmation-dialogs/deleteConfirmation/DeleteConfirmation';
import DeleteConfirmationGlass from 'ui-component/confirmation-dialogs/deleteConfirmation/DeleteConfirmationGlass';
import { FaCircleCheck } from 'react-icons/fa6';
import usePermissions from 'hooks/usePermissions';
import { CommonDateOnlyFormat } from 'helpers/common.helper';
import { CountriesUIFlag } from 'ui-component/CountriesUI';
import {
	LaptopIcon,
	LaptopPlusIcon,
	MapIcon,
	SuspendedIcon,
	ContactInvoiceIcon,
	ClientPortalIcon
} from 'ui-component/custom-icons/icons';
import usePageTitle from 'hooks/usePageTitle';
import DemographicChartComponent from '../dashboard/Default/components/DemographicChartComponent';
import SortableHeader from 'ui-component/SortableHeader';
import NoRecordFoundIcon from 'assets/images/icons/no-records-found.svg';
import useIsMobile from 'utils/isMobileUtils';
import CreateClientPortalNotificationModal from './modals/CreateClientPortalNotificationModal';

// ✅ Clear
const index = () => {
	const [setPageTitle] = usePageTitle('Contacts');
	const history = useNavigate();
	const statusTypes = useSelector((state) => state.main.statusTypes);
	const dispatch = useDispatch();
	const [allPermissions, hasEveryPermission] = usePermissions();
	const [showMap, setShowMap] = useState(false);
	const [mapVisible, setMapVisible] = useState(false);
	const [showListBlocker, setShowListBlocker] = useState(false);

	const [searchField, setSearchField] = useState();
	const [searchValue, setSearchValue] = useState();
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [filters, setFilters] = useState([]);
	const [activeFilters, setActiveFilters] = useState({});
	const [sortByFilter, setSortByFilter] = useState();
	const [showCreateClientPortalModal, setShowCreateClientPortalModal] = useState(false);
	const [createClientPortalModalDetails, setCreateClientPortalModalDetails] = useState(null);


	const storedSearch = useSelector((state) => state.main.searchContact);
	const storedFilter = useSelector((state) => state.main.filterContact);

	const [dataList, setDataList] = useState([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const tableRef = useRef();
	const [showPreloadingRows, setShowPreloadingRows] = useState(true);
	const [totalRecords, setTotalRecords] = useState(0);


	const [checkedItems, setCheckedItems] = useState([]);
	const [showDeleteButton, setShowDeleteButton] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);


	useEffect(() => {
		// Only run fetchData after user stops typing (debounced by searchTimeout)
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		const timeout = setTimeout(() => {
			const fetchData = async () => {
				if (storedSearch) {
					await handleSearch({ target: { value: storedSearch } });
				}
				setDataList([]);
				initializeData();
				getFiltersList();

        		getContactCountryMap();
			};
			fetchData();
		}, 500); // 500ms debounce

		setSearchTimeout(timeout);

		// Cleanup on unmount or before next effect run
		return () => clearTimeout(timeout);
		// Only trigger when these change
	}, [storedSearch, searchField, storedFilter]);

	useEffect(() => {
		setIsLoading(true);
	}, [activeFilters])
	useEffect(() => {
		const handleScroll = () => {
			if (!tableRef.current || showPreloadingRows || !hasMore) return;
			const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
			// Trigger only when user reaches the bottom (with a small threshold)
			if (scrollTop + clientHeight >= scrollHeight - 10) {
				loadMoreData();
			}
		};
		const currentRef = tableRef.current;
		if (currentRef) {
			currentRef.addEventListener('scroll', handleScroll);
		}
		return () => {
			if (currentRef) {
				currentRef.removeEventListener('scroll', handleScroll);
			}
		};
	}, [showPreloadingRows, hasMore, dataList]);


	const loadMoreData = () => {
		if (showPreloadingRows || !hasMore) return;
		setShowPreloadingRows(true);
		let request = {};
		request.page = page + 1;
		request.search = searchField?.length > 2 ? searchField : null;

		api.getContactLists(request, activeFilters, searchField, sortByFilter).then((res) => {
			const nextPage = res?.data?.data?.meta?.current_page || request.page;
			const lastPage = res?.data?.data?.meta?.last_page || nextPage;
			if (nextPage > lastPage || res?.data?.data?.data?.length === 0) {
				setHasMore(false);
			} else {
				setDataList(prev => [...prev, ...res?.data?.data?.data || []]);
				setPage(nextPage);
			}
			setShowPreloadingRows(false);
		});
	};


	const initializeData = () => {
		// Initial load
		let request = {}
		request.page = 1;
		request.search = searchField?.length > 2 ? searchField : null;
		api.getContactLists(request, activeFilters, searchField, sortByFilter).then((res) => {
			setDataList([]);
			setTimeout(() => {
				setDataList(res?.data?.data?.data || []);

				setHasMore(true);
				setPage(1);
				dispatch(setLoading(false));
				setIsLoading(false);
				setShowPreloadingRows(false);
				setTotalRecords(res?.data?.data?.total || 0);
			}, 300);
		});

	}
	const [isLoading, setIsLoading] = useState(true);
	const [isMapLoading, setIsMapLoading] = useState(false);
	const [regionValues, setRegionValues] = useState(null);
	const [regionDatas, setRegionDatas] = useState(null);


    const getContactCountryMap = () => {
        setIsMapLoading(true);
        api.getContactCountryMap().then((res) => {

            const formattedWorldMap = res?.data?.reduce((acc, item) => {
                acc[item.country] = item.count;
                return acc;
            }, {});
            setRegionValues(formattedWorldMap);
            setRegionDatas(res.data);
            setIsMapLoading(false);
        });
    };


	const handleClose = () => {
		setShowDeleteModal(false);
	}

	const getFiltersList = () => {
		api.getContactFilter().then((res) => {
			setFilters(res.data);
		});
	};

	const handleSearch = (e) => {
		let text = e.target.value;
		
		setIsLoading(true);
		setSearchValue(text);
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		// Only update searchField after user stops typing for 500ms
		let timeout = setTimeout(() => {
			dispatch(storeSearchContact(text));
			setSearchField(text);
		}, 500);

		setSearchTimeout(timeout);

	};

	const isMobile = useIsMobile();


	const updateSortByStatus = (name) => {
		if (sortByFilter?.field == name) {
			setSortByFilter((e) => ({
				...e,
				direction: e?.direction == 'asc' ? 'desc' : 'asc'
			}));
		} else {
			setSortByFilter((e) => ({
				field: name,
				direction: 'asc'
			}));
		}
	};
	


	useEffect(() => {
		if (showMap) setMapVisible(true);
	}, [showMap]);



	const getClientPortalAccessToken = (id) => {

		toast.promise(
			api.getClientPortalAccessToken(id).then((res) => {
				if (res?.status) {
					const clientToken = res?.data?.client_token;
					dispatch(storeClientDetails({ ...res, stayLoggedIn: true }));
					toast.success('Client login successfully');
					if (res?.data?.is_approved === 2) {
						openInNewTab('/client/dashboard');
					} else if (res?.data?.is_approved === 0) {
						openInNewTab('/client/company-details');
					} else {
						openInNewTab('/client/company-verified');
					}
					return res; // success
				} else {
					throw new Error(res?.message || 'Failed to access client portal');
				}
			}),
			{
				loading: 'Accessing Client Portal...',
				success: (data) => `${data.message || 'Accessed client portal.'}`,
				error: <b>Failed to access client portal</b>
			}
		);
	}

	const createClientPortalAccessToken = (id, index) => {
		setShowListBlocker(true)
		toast.promise(
			api.createClientPortalAccessToken(id).then((res) => {
				setShowListBlocker(false)
				if (res?.status) {
					setDataList(prev =>
						prev.map((item, idx) =>
							idx === index ? { ...item, has_client_portal: true } : item
						)
					);
					if (res?.other_data) {
						setShowCreateClientPortalModal(true)
						setCreateClientPortalModalDetails(res?.other_data)
					} else {
						setShowCreateClientPortalModal(false)
						setCreateClientPortalModalDetails(null)
					}
					return res; // success
				} else {
					throw new Error(res?.message || 'Failed to create client portal');
				}
			}),
			{
				loading: 'Creating Client Portal...',
				success: (data) => `Client portal created successfully'`,
				error: <b>Failed to create client portal</b>
			}
		);
	}

    const openInNewTab = (url) => {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
    };
	useEffect(() => {
		setShowDeleteButton(checkedItems.length > 0);
	}, [checkedItems]);


    const viewUserData = (id) => {
        const currentUser = dataList.filter((item) => item.id === id);
        history(`/contacts/${id}`);
	};
	
	const handleDeleteMultipleRecords = () => {
		
	}

	return (
		<>
			<div className="row justify-content-center m-0">
                <div className="medium-sized-container" style={{ maxWidth: '900px' }}>
					
					<div className="col-xl-12 col-lg-12 p-0">
						<div className="show-on-mobile new-page-header" style={{ position: 'relative' }}>
							<div className="header-bubble" />
							<div className="d-flex flex-column gap-2 " style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
								<h5 style={{ textAlign: 'center' }}>Contacts</h5>
								<div
									style={{
										paddingLeft: 20,
										paddingRight: 20,
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										gap: 10,
										paddingBottom: 4
									}}
								>
									<div className="header-option-container">
										<a
											href="javascript:void(0);"
											className="header-option active"
											onClick={() => {
												setShowMap(!showMap)
											}}
										>
											<MapIcon height="16px" width="16px" /> Map View 
										</a>
										<Link to={'/contacts/portal'} className="header-option active">
											<LaptopIcon height={'16'} width={'16'} /> Portal
										</Link>
										<Link to={'/contacts/create'} className="header-option active" style={{ color: '#316aff' }}>
											<FaPlus /> New
										</Link>
									</div>
								</div>
							</div>
						</div>
						<div className="section-head-new pl-0 hide-on-mobile" style={{ margin: '10px 0' }}>
							<div>
								<button
									className="btn btn-primary btn-outlined-main"
									onClick={() => {
										setShowMap(!showMap)
									}}
								>
									<MapIcon height="16px" width="16px" />  { showMap ? 'Hide' : 'Show' } Map View
								</button>
							</div>
							<div className="actions">
								<Link to={'/contacts/portal'} className="btn btn-primary btn-outlined-main">
									<LaptopIcon height={'16'} width={'16'} /> Portal Approval
								</Link>
								<PermissionAllowed permissions={['Create Contact (Button)']}>
									<Link to={'/contacts/create'} className="btn btn-primary btn-outlined-main-primary">
										<FaPlus /> New Contact
									</Link>
								</PermissionAllowed>
							</div>
						</div>
						<div
							style={{
								maxHeight: showMap ? 600 : 0, // adjust 500 to your modal's max height
								overflow: 'hidden',
								transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
								marginTop: 0,
								marginBottom: showMap ? '15px' : 0,
								opacity: showMap ? 1 : 0,
								transitionProperty: 'max-height, opacity, margin',
							}}
							onTransitionEnd={() => {
								if (!showMap) setMapVisible(false);
							}}
						>
							{mapVisible && (
								<>
									<DemographicChartComponent
										mapCount={regionValues}
										mapList={regionDatas}
										isLoading={isMapLoading}
									/>
									<div style={{ marginBottom: '10px' }}></div>
								</>
							)}
						</div>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: 10,
								paddingTop: 0,
								paddingBottom: 5,
								paddingLeft: 20,
								paddingRight: 20
							}}
							className="show-on-mobile"
						>
							<form className="form-inline navbar-search" style={{ flex: 1 }}>
								<div style={{ position: 'relative', width: '100%' }}>
									<input
										type="text"
										className="form-control bg-white border"
										placeholder="Search"
										aria-label="Search"
										aria-describedby="basic-addon2"
										value={searchValue || ''}
										onChange={handleSearch}
										style={{ width: '100%', paddingRight: 36 }}
									/>
									<span
										className="searchIcon"
										style={{
											position: 'absolute',
											right: 10,
											top: '50%',
											transform: 'translateY(-50%)',
											color: '#888',
											pointerEvents: 'none'
										}}
									>
										<FaSearch />
									</span>
								</div>
							</form>
							<div style={{ flexShrink: 0 }}>
								<Filter
									activeFilters={activeFilters}
									onSubmit={setActiveFilters}
									filters={filters}
									multi={undefined}
									source={`contact`}
								/>
							</div>
						</div>
						<div className={`card shadow-sm  bg-white table-card ${Object.keys(activeFilters).length !== 0 ? 'has-filter' : '' } ${isMobile ? 'admin-shorter-table' : ''}`}>
							{
								showListBlocker && (
									<div className="contact-list-blocker">
										<FaSpinner className="fa-spin" />
										<div>Please wait...</div>
									</div>
								)
							}
							<div className=" align-items-center justify-content-between TableSerachBar">
								<div>
									<Filter
										activeFilters={activeFilters}
										onSubmit={setActiveFilters}
										filters={filters}
										multi={undefined}
										source={'contact'}
									/>
								</div>
								<form className="form-inline navbar-search" style={{ display: 'flex', gap: '5px' }} onSubmit={(e) => e.preventDefault()}>
									
									<div className="input-group">
										<input
											type="text"
											className="form-control bg-white border"
											placeholder="Search"
											aria-label="Search"
											aria-describedby="basic-addon2"
											value={searchValue || ''}
											onChange={handleSearch}
											style={{ width: '100%' }}
										/>
										<span className="searchIcon">
											<FaSearch />
										</span>
									</div>
								</form>
							</div>
							<div className="estimation-table-area">
								<div className="table-side">

									{
										!isMobile ? (
											<div
												className="table-responsive-xl tableData estimationList"
												ref={tableRef}
												style={{
													height: 'calc(100vh - 260px)',
													overflowY: 'auto',
													scrollbarWidth: 'thin',
													scrollbarColor: '#0966ff transparent'

												}}
											>
													<table className="table  min-width-unset tbl-space pm-table d-none d-md-table">
														<thead>
															<tr className="sticky-header">
																
																<th scope="col" style={{ width: '80px' }}>
																	<SortableHeader
																		name="country"
																		currentSortBy={sortByFilter}
																		onClick={() => {
																			updateSortByStatus('country');
																		}}
																	>
																		Country
																	</SortableHeader>
																</th>

																<th scope="col" style={{ width: '350px' }}>
																	<SortableHeader
																		name="company"
																		currentSortBy={sortByFilter}
																		onClick={() => {
																			updateSortByStatus('company');
																		}}
																	>
																		Company
																	</SortableHeader>
																</th>
																<th scope="col" style={{ width: '150px' }}>
																	Contact
																</th>
																<th scope="col"></th>
															</tr>
														</thead>
														<tbody>
															{isLoading ? (
																[...Array(50)].map((e, i) => (
																	<tr style={{ height: '35px' }} key={i}>
																		{[...Array(4)].map((e, i) => (
																			<td>
																				<Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
																			</td>
																		))}
																	</tr>
																))
															) : (
																<>
																	{dataList?.length > 0 && (
																		dataList?.map((item, index) => {
																			const company_name = item?.name?.length <= 40 ? item.name : item.name?.slice(0, 40) + '...';
																			return (
																				<tr style={{ background: `${checkedItems.includes(item.id) ? '#d9e7ff' : 'revert-layer'}` }} key={index}>
																				
																					<td
																						className={'cursor-pointer align-middle text-center font14'}
																						onClick={() => {
																							if (hasEveryPermission(['View Contact (Button)'])) {
																								viewUserData(item.id);
																							}
																						}}
																						style={{
																							cursor: hasEveryPermission(['View Contact (Button)']) ? 'pointer' : 'not-allowed',
																							height: '35px'
																						}}
																					>
																						{item?.country ? (
																							<div
																								style={{
																									fontSize: '20px',
																									background: '#f2f4f6',
																									borderRadius: '8px',
																									width: '37px'
																								}}
																							>
																								<CountriesUIFlag countryName={`${item?.country}`} />
																							</div>
																						) : (
																							<div
																								style={{
																									fontSize: '20px',
																									background: '#f2f4f6',
																									borderRadius: '8px',
																									width: '37px',
																									height: '37px'
																								}}
																							></div>
																						)}
																					</td>
																					<td
																						className={'cursor-pointer align-middle font14'}
																						onClick={() => {
																							if (hasEveryPermission(['View Contact (Button)'])) {
																								viewUserData(item.id);
																							}
																						}}
																						style={{
																							cursor: hasEveryPermission(['View Contact (Button)']) ? 'pointer' : 'not-allowed',
																							height: '35px'
																						}}
																					>
																						<div>{company_name || '-'}</div>
																					</td>
																					<td
																						className={'cursor-pointer align-middle font14'}
																						onClick={() => {
																							if (hasEveryPermission(['View Contact (Button)'])) {
																								viewUserData(item.id);
																							}
																						}}
																						style={{
																							cursor: hasEveryPermission(['View Contact (Button)']) ? 'pointer' : 'not-allowed',
																							height: '35px'
																						}}
																					>
																						{item?.contact_person}
																					</td>
																					<td
																						className={'cursor-pointer align-middle font14'}
																					>
																						<div className="w-full d-flex justify-content-center align-items-center gap-6">
																							<div style={{ width: '60px' }}>
																								{item?.number_of_invoices > 0 && (
																									<Tooltip
																										title={
																											<span style={{ fontSize: '12px' }}>
																												Number of Invoices: {item?.number_of_invoices}
																												<br />
																												Last ordered:{' '}
																												{item?.last_ordered
																													? moment(item?.last_ordered).format(CommonDateOnlyFormat)
																													: 'N/A'}
																											</span>
																										}
																										arrow
																										placement="top"
																									>
																										<div
																											style={{
																												fontSize: '20px',
																												background: '#f2f4f6',
																												borderRadius: '8px',
																												padding: '0 10px'
																											}}
																											className="d-flex align-items-center justify-content-center gap-3"
																										>
																											<div>
																												<ContactInvoiceIcon height="15px" width="15px" />
																											</div>
																											<div style={{ fontSize: '13px', fontWeight: '500' }}>
																												{item?.number_of_invoices || 0}
																											</div>
																										</div>
																									</Tooltip>
																								)}
																							</div>
																							<div
																								style={{
																									width: '10px',
																									display: 'flex',
																									justifyContent: 'center',
																									alignItems: 'center'
																								}}
																							>
																								<>
																									{item?.verification_level?.percentage === '100' && (
																										<Tooltip
																											title={`Verification percentage: ${item?.verification_level?.percentage}%`}
																											arrow
																											placement="top"
																										>
																											<div
																												className="text-success"
																												style={{ fontSize: '17px', marginInlineEnd: '5px' }}
																											>
																												<FaCircleCheck />
																											</div>
																										</Tooltip>
																									)}

																									{item?.status === 'Suspended' && (
																										<Tooltip title={`Suspended`} arrow placement="top">
																											<div
																												className="text-danger"
																												style={{ fontSize: '17px', marginInlineEnd: '5px' }}
																											>
																												<SuspendedIcon height="15px" width="15px" />
																											</div>
																										</Tooltip>
																									)}
																								</>
																							</div>
																							<div style={{ width: '30px' }}>
																								{!item?.has_client_portal ? (
																									<Tooltip title={'Create Client Portal'} arrow placement="top">
																										<button
																											className="btn btn-primary btn-primary-transparent  white-var btn-sm cursor-pointer"
																											style={{ width: '30px' }}
																											onClick={(e) => {
																												e.stopPropagation();
																												if (item?.email === null || item?.email === '') {
																													toast.error(
																														'Cannot create client portal. Email address is missing for this contact.'
																													);
																												} else if (item?.contact_person === null || item?.contact_person === '') {
																													toast.error(
																														'Cannot create client portal. Contact person is missing for this contact.'
																													);
																												} else {
																													createClientPortalAccessToken(item?.id, index);
																												}
																											}}
																										>
																											<div style={{ marginTop: '4px' }}>
																												<LaptopPlusIcon height={'18'} width={'18'} />
																											</div>
																										</button>
																									</Tooltip>
																								) : (
																									<Tooltip title={'Access Client Portal'} arrow placement="top">
																										<button
																											className="btn btn-primary btn-primary-transparent  white-var btn-sm cursor-pointer"
																											style={{ width: '30px' }}
																											onClick={(e) => {
																												e.stopPropagation();
																												getClientPortalAccessToken(item?.id);
																											}}
																										>
																											<ClientPortalIcon height={'18'} width={'18'} />
																										</button>
																									</Tooltip>
																								)}
																							</div>
																						</div>
																					</td>
																				</tr>
																			);
																		})
																	)}
																	{showPreloadingRows && (
																		[...Array(100)].map((e, i) => (
																			<tr style={{ height: '35px' }} key={i}>
																				{[...Array(4)].map((e, i) => (
																					<td>
																						<Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
																					</td>
																				))}
																			</tr>
																		))
																	)}
																	{(dataList?.length < 1 && !isLoading) && (
																		<tr className="no-hover">
																			<td colSpan={9}>
																				<div className="not-found-container">
																					<div className="not-found-icon">
																						<img src={NoRecordFoundIcon} style={{ height: '200px', width: '200px' }} />
																					</div>
																					<h6 className="not-found-text">No Data Available</h6>
																					
																				</div>
																			</td>
																		</tr>
																	)}
																</>
															)}
														</tbody>
												</table>
											</div>
										) : (
											<div
												className="table-responsive-xl tableData estimationList"
												ref={tableRef}
												style={{
													height: `calc(100dvh - ${Object.keys(activeFilters).length === 0 ? '315px' : '345px' })`,
													overflowY: 'auto',
													display: 'flex',
													flexDirection: 'column'
												}}
											>
												
												{isLoading ? (
													[...Array(7)].map((_, i) => (
														<div className="inv-cards-list d-md-none" style={{ padding: '12px 0' }}>
															{[...Array(15)].map((_, idx) => (
																<div
																	key={idx}
																	className="order-list-card"
																	style={{
																		background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
																		marginBottom: 10,
																		borderRadius: 12,
																		padding: 16,
																		minHeight: 90,
																		display: 'flex',
																		flexDirection: 'column',
																		gap: 10
																	}}
																>
																	{[...Array(3)].map((__, i) => (
																		<div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
																			<Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
																		</div>
																	))}
																</div>
															))}
														</div>
													))
												) : (
													<>
														{dataList.map((item, index) => {
															const company_name = item?.name?.length <= 40 ? item.name : item.name?.slice(0, 40) + '...';
															return (
																<div
																	key={index}
																	className="contact-mobile-card"
																	style={{
																		borderBottom: '1px solid #eee',
																		padding: '8px 16px',
																		display: 'flex',
																		justifyContent: 'space-between'
																	}}
																	onClick={() => {
																		if (hasEveryPermission(['View Contact (Button)'])) {
																			viewUserData(item.id);
																		}
																	}}
																>
																	<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
																		<div
																			style={{
																				fontSize: '20px',
																				background: '#f2f4f6',
																				borderRadius: '8px',
																				width: '37px',
																				display: 'flex',
																				justifyContent: 'center',
																				alignItems: 'center'
																			}}
																		>
																			<CountriesUIFlag countryName={`${item?.country}`} />
																		</div>
																		<div style={{ fontWeight: 400, fontSize: 13 }}>
																			{company_name?.length > 20 ? company_name.slice(0, 20) + '...' : company_name || '-'}
																		</div>
																	</div>
																	<div style={{ display: 'flex', width: 150 }}>
																		<div style={{ width: '25%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
																			{item?.number_of_invoices > 0 && (
																				<Tooltip title={`Number of Invoices: ${item?.number_of_invoices}`} arrow placement="top">
																					<div
																						style={{
																							fontSize: '20px',
																							background: '#f2f4f6',
																							borderRadius: '8px',
																							padding: '3px 6px',
																							display: 'flex',
																							alignItems: 'center',
																							gap: '5px'
																						}}
																					>
																						<ContactInvoiceIcon height="15px" width="15px" />
																						<span style={{ fontSize: '13px', fontWeight: '500' }}>{item?.number_of_invoices || 0}</span>
																					</div>
																				</Tooltip>
																			)}
																		</div>
																		<div style={{ width: '25%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
																			<>
																				{item?.verification_level?.percentage === '100' && (
																					<Tooltip
																						title={`Verification percentage: ${item?.verification_level?.percentage}%`}
																						arrow
																						placement="top"
																					>
																						<div className="text-success" style={{ fontSize: '17px', marginInlineEnd: '5px', marginLeft: '14px' }}>
																							<FaCircleCheck />
																						</div>
																					</Tooltip>
																				)}

																				{item?.status === 'Suspended' && (
																					<Tooltip title={`Suspended`} arrow placement="top">
																						<div className="text-danger" style={{ fontSize: '17px', marginInlineEnd: '5px' }}>
																							<SuspendedIcon
																								height="15px"
																								width="15px"
																							/>
																						</div>
																					</Tooltip>
																				)}
																			</>
																		</div>
																		<div style={{ width: '25%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
																			{item?.has_client_portal && (
																				<Tooltip title={'Access Client Portal'} arrow placement="top">
																					<button
																						className="btn btn-primary btn-primary-transparent white-var btn-sm cursor-pointer"
																						style={{ width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
																						onClick={() => {
																							getClientPortalAccessToken(item?.id)
																						}}
																					>
																						<ClientPortalIcon height={'18'} width={'18'} />
																					</button>
																				</Tooltip>
																			)}
																		</div>
																	</div>
																</div>
															);	
														})}

																
																										
														{showPreloadingRows && (
															[...Array(100)].map((_, idx) => (
																<div
																	key={idx}
																	className="order-list-card"
																	style={{
																		background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
																		marginBottom: 10,
																		borderRadius: 12,
																		padding: 16,
																		minHeight: 90,
																		display: 'flex',
																		flexDirection: 'column',
																		gap: 10
																	}}
																>
																	{[...Array(3)].map((__, i) => (
																		<div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
																			<Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
																		</div>
																	))}
																</div>
															))
														)}
														{(dataList?.length < 1 && !isLoading) && (
															<div className="w-100">
																<div className="not-found-container">
																	<div className="not-found-icon">
																		<TbDatabaseExclamation />
																	</div>
																	<h6 className="not-found-text">No Data Available</h6>
				
																	<PermissionAllowed permissions={['Create New Contact (Action)']}>
																		<Link to={'/contacts/create'} className="btn btn-primary btn-outlined-main" style={{ marginTop: 12 }}>
																			Create Contact
																		</Link>
																	</PermissionAllowed>
																</div>
															</div>
														)}
													</>
												)}
											</div>
										)
									}
									{isLoading ? (
										<div className="inv-cards-list d-md-none" style={{ padding: '12px 0' }}>
											{[...Array(15)].map((_, idx) => (
												<div
													key={idx}
													className="order-list-card"
													style={{
														background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
														marginBottom: 10,
														borderRadius: 12,
														padding: 16,
														minHeight: 90,
														display: 'flex',
														flexDirection: 'column',
														gap: 10
													}}
												>
													{[...Array(3)].map((__, i) => (
														<div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
															<Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
														</div>
													))}
												</div>
											))}
										</div>
									) : (
										''
									)}
									<div
										className="hide-on-mobile"
										style={{
											padding: '10px 0 0 20px',
											borderTop: '1px solid #e0e0e0e0',
											display: 'flex',
											gap: '5px',
											justifyContent: 'space-between',
											height: '40px',
										}}
									>

										<div className="flex align-items-center" style={{ gap: '10px' }}>
											{
												showDeleteButton && (
													<>
														<button 
															className="btn btn-danger btn-outlined-main-danger white-va"
															onClick={() => {
																setShowDeleteModal(true);
															}}
														>
															Delete
														</button>
														<div>
															<span className="text-gray-800">Selected:</span>{' '}
															<span className="fw-500">
																{checkedItems.length ? checkedItems.length.toLocaleString('en-US') : 0}
															</span>
														</div>
													</>
												)
											}
										</div>
										<div className="flex align-items-center ml-auto" style={{ gap: '5px', paddingRight: '25px' }}>
											<div className="text-gray-800">Total Record:</div>
											<div className="fw-500">
												{isLoading ? (
													<div style={{ width: '100px' }}>
														<Skeleton variant="rounded" width={'100%'} height={20}></Skeleton>
													</div>
												) : (
													totalRecords.toLocaleString('en-US')
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>


			{showDeleteModal && (
				<DeleteConfirmationGlass
					show={showDeleteModal}
					handleClose={handleClose}
					customDeleteButtonText={`Delete`}
					handleDelete={handleDeleteMultipleRecords}
					title={`Confirmation`}
					message={`Are you sure you want to delete ${checkedItems.length} selected records from purchasing notes?`}
				/>
			)}
			{
				showCreateClientPortalModal && (
					<CreateClientPortalNotificationModal
						openPopup={showCreateClientPortalModal}
						handleClose={() => {
							setShowCreateClientPortalModal(false)
							setCreateClientPortalModalDetails(null)
						}}
						details={createClientPortalModalDetails}
					/>
				)
			}
		</>
	);
};

export default index;
